import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import api from '../config/api';
import PartnerPlanWall from '../components/PartnerPlanWall';
import {
  PartnerEmptyState,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import { canUsePartnerClientInvite, isPartnerPlanLimitError } from '../utils/partnerPlan';

const fieldSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputLabel-root': { color: DOCUMENT.ink3 },
  '& .MuiInputLabel-root.Mui-focused': { color: DOCUMENT.accent },
};

const CLIENT_GRID_COLUMNS = {
  xs: 'minmax(0, 1fr) 6.5rem',
  md: 'minmax(0, 1.8fr) 6.5rem minmax(7rem, 1fr)',
};

const clientGridSx = {
  display: 'grid',
  gridTemplateColumns: CLIENT_GRID_COLUMNS,
  columnGap: { xs: 1.5, md: 2 },
  alignItems: 'center',
};

const whenLabel = (value, t) => {
  if (!value) return t('partnerTeam.dash');
  try {
    return t('partnerProfile.seenWhen', {
      when: formatDistanceToNow(parseISO(String(value).replace(' ', 'T')), { addSuffix: false }),
    });
  } catch {
    return t('partnerTeam.dash');
  }
};

const PartnerClientInvite = ({ embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entitlements, loading: entitlementsLoading } = usePartnerEntitlements();
  const isBespoke = canUsePartnerClientInvite(entitlements);

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planEntitlements, setPlanEntitlements] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('partnerToken')}`,
  });

  const loadInvite = async () => {
    try {
      setLoading(true);
      if (!localStorage.getItem('partnerToken')) {
        navigate('/partner/login');
        return;
      }
      const response = await api.get('/partners/client-invite', { headers: getAuthHeaders() });
      setInvite(response.data);
      setError('');
    } catch (err) {
      if (isPartnerPlanLimitError(err) || err.response?.data?.code === 'PLAN_REQUIRED') {
        setPlanEntitlements(err.response?.data?.entitlements || entitlements);
        setInvite(null);
      } else {
        setError(err.response?.data?.error || t('partnerClientInvite.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entitlementsLoading) return;
    if (!isBespoke) {
      setLoading(false);
      setInvite(null);
      return;
    }
    loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitlementsLoading, isBespoke]);

  const handleCopy = async () => {
    if (!invite?.url) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('partnerClientInvite.copyFailed'));
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    setError('');
    try {
      const response = await api.post('/partners/client-invite/rotate', {}, { headers: getAuthHeaders() });
      setInvite(response.data);
      setSuccess(t('partnerClientInvite.rotateSuccess'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerClientInvite.rotateFailed'));
    } finally {
      setRotating(false);
    }
  };

  const handleEmail = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(
        '/partners/client-invite/email',
        { email: email.trim() },
        { headers: getAuthHeaders() }
      );
      if (Array.isArray(response.data?.clients)) {
        setInvite((current) => ({ ...current, ...response.data, clients: response.data.clients }));
      } else {
        await loadInvite();
      }
      setSuccess(t('partnerClientInvite.emailSuccess'));
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerClientInvite.emailFailed'));
    } finally {
      setSending(false);
    }
  };

  const inviteContent = (
    <>
      {!embedded && (
        <PartnerPageHeader
          title={t('partnerClientInvite.title')}
          subtitle={t('partnerClientInvite.subtitle')}
        />
      )}

      {embedded && error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {embedded && success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      {(entitlementsLoading || (isBespoke && loading)) && <PartnerLoading />}

      {!entitlementsLoading && !isBespoke && (
        <Box sx={{ pt: 4, maxWidth: 520 }}>
          <Typography sx={{ fontSize: 15, color: DOCUMENT.ink2, lineHeight: 1.7, mb: 2.5 }}>
            {t('partnerClientInvite.bespokeOnly')}
          </Typography>
          <Button sx={partnerOxbloodBtnSx} onClick={() => setPlanWallOpen(true)}>
            {t('partnerClientInvite.viewPlans')}
          </Button>
        </Box>
      )}

      {isBespoke && invite && (
        <>
        <Box sx={{ pt: 4, maxWidth: 560 }}>
          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.19em',
              textTransform: 'uppercase',
              color: DOCUMENT.ink3,
              mb: 1,
            }}
          >
            {t('partnerClientInvite.linkLabel')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              pb: 1.5,
              borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
            }}
          >
            <Typography
              data-testid="client-invite-url"
              sx={{ fontSize: 13.5, color: DOCUMENT.ink, wordBreak: 'break-all', flex: 1, minWidth: 0 }}
            >
              {invite.url}
            </Typography>
            <Button sx={partnerOxbloodBtnSx} onClick={handleCopy}>
              {copied ? t('partnerClientInvite.copied') : t('partnerClientInvite.copy')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.25 }}>
            <Box
              component="button"
              type="button"
              onClick={handleRotate}
              disabled={rotating}
              sx={partnerLinkSx}
            >
              {rotating ? t('partnerClientInvite.rotating') : t('partnerClientInvite.rotate')}
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.19em',
              textTransform: 'uppercase',
              color: DOCUMENT.ink3,
              mt: 4,
              mb: 1,
            }}
          >
            {t('partnerClientInvite.emailLabel')}
          </Typography>
          <Box
            component="form"
            onSubmit={handleEmail}
            sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <TextField
              variant="standard"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              label={t('partnerClientInvite.emailAddress')}
              sx={{ ...fieldSx, flex: 1, minWidth: 220 }}
            />
            <Button type="submit" sx={partnerOxbloodBtnSx} disabled={sending}>
              {sending ? t('partnerClientInvite.sending') : t('partnerClientInvite.send')}
            </Button>
          </Box>

          <PartnerNote>{t('partnerClientInvite.note')}</PartnerNote>
        </Box>

        <Box sx={{ pt: 5 }}>
          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.19em',
              textTransform: 'uppercase',
              color: DOCUMENT.ink3,
              mb: 1.5,
            }}
          >
            {t('partnerClientInvite.clientsLabel', { company: invite.name })}
          </Typography>
          {(invite.clients || []).length === 0 ? (
            <PartnerEmptyState
              title={t('partnerClientInvite.emptyClientsTitle')}
              body={t('partnerClientInvite.emptyClientsBody')}
            />
          ) : (
            <Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}` }}>
              <Box
                sx={{
                  ...clientGridSx,
                  display: { xs: 'none', md: 'grid' },
                  py: 1.25,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                {[
                  t('partnerClientInvite.client'),
                  t('partnerClientInvite.status'),
                  t('partnerClientInvite.since'),
                ].map((label) => (
                  <Typography
                    key={label}
                    sx={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
              {(invite.clients || []).map((client) => (
                <Box
                  key={client.id || client.email}
                  data-testid="partner-client-row"
                  sx={{
                    ...clientGridSx,
                    py: 2,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, color: DOCUMENT.ink }}>
                      {client.username || client.email}
                    </Typography>
                    {client.username && (
                      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.35 }}>
                        {client.email}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                    {client.status === 'joined'
                      ? t('partnerClientInvite.statusJoined')
                      : t('partnerClientInvite.statusInvited')}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, display: { xs: 'none', md: 'block' } }}>
                    {whenLabel(client.status === 'joined' ? client.joined_at : client.invited_at, t)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        </>
      )}

      {isBespoke && !loading && !invite && !error && (
        <Alert severity="info" sx={{ mt: 3 }}>{t('partnerClientInvite.loadFailed')}</Alert>
      )}

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        reason="clients"
        entitlements={planEntitlements || entitlements}
      />
    </>
  );

  if (embedded) {
    return inviteContent;
  }

  return (
    <PartnerPageShell
      error={error}
      onClearError={() => setError('')}
      success={success}
      onClearSuccess={() => setSuccess('')}
    >
      {inviteContent}
    </PartnerPageShell>
  );
};

export default PartnerClientInvite;
